import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Play, RotateCw, AlertTriangle, CheckCircle2, Clock, Terminal, ChevronRight, Video, FileText, XCircle } from 'lucide-react'
import { Post, PipelineEvent, PipelineRun } from '../pages/types'
import { API_BASE } from '../config'

interface ScraperDashboardProps {
  /** Firestore "source" value used to scope posts/runs to this scraper. */
  source: string
  /** Relative path appended to API_BASE to trigger a manual run, e.g. "/scrape" or "/scrape/ctolarsson". */
  scrapeEndpoint: string
  pageTitle: string
  pageDescription: string
  triggerLabel: string
  /** Link target for "View All" on the recent-items card. */
  postsLinkPath: string
  /** Copy shown in the empty-state / metric labels to match the content shape (e.g. "post" vs "report"). */
  itemNounSingular: string
  itemNounPlural: string
}

export default function ScraperDashboard({
  source,
  scrapeEndpoint,
  pageTitle,
  pageDescription,
  triggerLabel,
  postsLinkPath,
  itemNounSingular,
  itemNounPlural,
}: ScraperDashboardProps) {
  const [posts, setPosts] = useState<Post[]>([])
  const [loading, setLoading] = useState(true)
  const [scraping, setScraping] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [latestRun, setLatestRun] = useState<PipelineRun | null>(null)

  const isRunActive = latestRun?.status === 'running' || latestRun?.status === 'queued'

  const formatEventTime = (value: any) => {
    if (!value) return '--:--:--'
    const date = typeof value === 'string' ? new Date(value) : new Date(value)
    if (Number.isNaN(date.getTime())) return '--:--:--'
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  }

  const eventLevelClass = (level: PipelineEvent['level']) => {
    switch (level) {
      case 'success':
        return 'text-emerald-400'
      case 'warning':
        return 'text-amber-400'
      case 'error':
        return 'text-rose-400'
      default:
        return 'text-cyan-400'
    }
  }

  const fetchData = async () => {
    try {
      setError(null)
      const [postsRes, runRes] = await Promise.all([
        fetch(`${API_BASE}/posts?limit=8&source=${encodeURIComponent(source)}`),
        fetch(`${API_BASE}/runs/latest?source=${encodeURIComponent(source)}`),
      ])

      if (!postsRes.ok || !runRes.ok) {
        throw new Error('Failed to retrieve system status')
      }

      const postsData = await postsRes.json()
      const runData = await runRes.json()

      setPosts(postsData.posts || [])
      setLatestRun(runData.run || null)
    } catch (err: any) {
      setError(err.message || 'Server connection failed')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source])

  useEffect(() => {
    if (!isRunActive && !scraping) return

    const interval = setInterval(fetchData, 3000)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunActive, scraping])

  useEffect(() => {
    if (scraping && latestRun && !isRunActive) {
      setScraping(false)
    }
  }, [isRunActive, latestRun, scraping])

  const triggerManualScrape = async () => {
    if (scraping) return
    setScraping(true)
    setError(null)
    try {
      const res = await fetch(`${API_BASE}${scrapeEndpoint}`, { method: 'POST' })
      if (!res.ok) {
        throw new Error('Manual scrape run failed')
      }
      const data = await res.json()
      setLatestRun((prev) => ({
        id: data.run_id,
        status: 'running',
        source,
        current_step: 'queued',
        posts_found: prev?.posts_found || 0,
        posts_processed: 0,
        posts_skipped: 0,
        posts_failed: 0,
        events: [
          {
            id: 'local-start',
            step: 'queued',
            message: 'Manual scrape run submitted',
            level: 'info',
            created_at: new Date().toISOString(),
          },
        ],
      }))
      await fetchData()
    } catch (err: any) {
      setError(err.message || 'Scrape execution encountered an error')
      setScraping(false)
    }
  }

  // Calculate metrics
  const totalCount = posts.length
  const completedCount = posts.filter((p) => p.status === 'completed').length
  const failedCount = posts.filter((p) => p.status === 'failed').length
  const processingCount = posts.filter((p) => p.status === 'processing').length

  const lastPost = posts[0]
  const visibleEvents = latestRun?.events?.slice(-80) || []

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold tracking-tight bg-gradient-to-r from-white via-slate-200 to-slate-400 bg-clip-text text-transparent">
            {pageTitle}
          </h2>
          <p className="text-slate-400 text-sm mt-1">{pageDescription}</p>
        </div>

        <button
          onClick={triggerManualScrape}
          disabled={scraping || isRunActive || loading}
          className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-200
            ${scraping || isRunActive
              ? 'bg-slate-900 border border-slate-800 text-slate-500 cursor-not-allowed'
              : 'bg-cyan-500 hover:bg-cyan-400 text-slate-950 shadow-[0_0_20px_rgba(6,182,212,0.3)] hover:scale-[1.02]'
            }`}
        >
          {scraping || isRunActive ? (
            <>
              <RotateCw size={16} className="animate-spin" />
              Pipeline Running...
            </>
          ) : (
            <>
              <Play size={16} fill="currentColor" />
              {triggerLabel}
            </>
          )}
        </button>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-sm flex items-center gap-3">
          <AlertTriangle size={18} className="flex-shrink-0" />
          <span><strong>Connection Alert:</strong> {error}</span>
        </div>
      )}

      {/* Metric Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="glass-panel rounded-2xl p-6 relative overflow-hidden group hover:border-slate-700/80 transition-all duration-300">
          <div className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Total {itemNounPlural}</div>
          <div className="text-4xl font-bold mt-3 tracking-tight font-mono text-white group-hover:text-cyan-400 transition-colors">
            {loading ? '...' : totalCount}
          </div>
          <div className="text-xs text-slate-400 mt-2 flex items-center gap-1">
            <Clock size={12} /> Live {itemNounPlural} in index
          </div>
        </div>

        <div className="glass-panel rounded-2xl p-6 relative overflow-hidden group hover:border-slate-700/80 transition-all duration-300">
          <div className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Processing Status</div>
          <div className="text-4xl font-bold mt-3 tracking-tight font-mono text-white flex items-center gap-2">
            {loading ? '...' : completedCount}
            {completedCount > 0 && <CheckCircle2 size={24} className="text-emerald-500 inline glow-emerald" />}
          </div>
          <div className="text-xs text-slate-400 mt-2 flex items-center gap-2">
            {failedCount > 0 && (
              <span className="text-rose-400 font-semibold flex items-center gap-0.5">
                {failedCount} failed
              </span>
            )}
            {processingCount > 0 && (
              <span className="text-cyan-400 font-semibold animate-pulse">
                {processingCount} active
              </span>
            )}
            {failedCount === 0 && processingCount === 0 && <span className="text-emerald-400">All runs successful</span>}
          </div>
        </div>

        <div className="glass-panel rounded-2xl p-6 relative overflow-hidden group hover:border-slate-700/80 transition-all duration-300">
          <div className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Last Run State</div>
          <div className="mt-3.5">
            {loading ? (
              <span className="text-slate-500 font-mono">...</span>
            ) : lastPost ? (
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold flex items-center gap-1 uppercase tracking-wider ${
                  lastPost.status === 'completed'
                    ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-400'
                    : lastPost.status === 'failed'
                    ? 'bg-rose-500/10 border-rose-500/25 text-rose-400'
                    : 'bg-cyan-500/10 border-cyan-500/25 text-cyan-400 animate-pulse'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    lastPost.status === 'completed' ? 'bg-emerald-400' : lastPost.status === 'failed' ? 'bg-rose-400' : 'bg-cyan-400 animate-ping'
                  }`} />
                  {lastPost.status}
                </span>
              </div>
            ) : (
              <span className="text-slate-500 font-mono">No history</span>
            )}
          </div>
          <div className="text-[10px] text-slate-500 truncate mt-3 font-mono">
            {lastPost?.title || ''}
          </div>
        </div>
      </div>

      {/* Main Grid: Recent Items & Pipeline Console */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Recent Items Card */}
        <div className="glass-panel rounded-2xl p-6 lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-white tracking-wide">Recent {itemNounPlural}</h3>
            <Link to={postsLinkPath} className="text-xs text-cyan-400 hover:text-cyan-300 font-semibold flex items-center gap-0.5">
              View All <ChevronRight size={14} />
            </Link>
          </div>

          <div className="divide-y divide-slate-850">
            {loading ? (
              <div className="py-8 text-center text-slate-500 text-sm">Loading database elements...</div>
            ) : posts.length === 0 ? (
              <div className="py-8 text-center text-slate-500 text-sm">No {itemNounPlural.toLowerCase()} scraped yet. Click manual run to start!</div>
            ) : (
              posts.map((post) => (
                <div key={post.id} className="py-3.5 flex items-center justify-between gap-4 group">
                  <div className="min-w-0 flex-1 space-y-1">
                    <a
                      href={post.display_url || post.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-semibold text-slate-200 hover:text-cyan-400 transition-colors truncate block"
                    >
                      {post.title}
                    </a>
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1 text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                        {post.content_type === 'video' ? (
                          <>
                            <Video size={10} className="text-cyan-400" /> Video
                          </>
                        ) : (
                          <>
                            <FileText size={10} className="text-blue-400" /> {itemNounSingular}
                          </>
                        )}
                      </span>
                      <span>•</span>
                      <span>
                        {post.processed_at
                          ? new Date(post.processed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                          : 'Pending'}
                      </span>
                    </div>
                  </div>

                  <span className={`text-[10px] px-2.5 py-0.5 rounded-full border font-bold uppercase tracking-wider ${
                    post.status === 'completed'
                      ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                      : post.status === 'failed'
                      ? 'bg-rose-500/10 border-rose-500/20 text-rose-400'
                      : 'bg-cyan-500/10 border-cyan-500/20 text-cyan-400 animate-pulse'
                  }`}>
                    {post.status}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Live pipeline observability */}
        <div className="glass-panel rounded-2xl p-6 flex flex-col space-y-4">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-lg font-bold text-white tracking-wide flex items-center gap-2">
              <Terminal size={16} className="text-cyan-400" /> Pipeline Console
            </h3>
            <span className={`text-[10px] px-2.5 py-1 rounded-full border font-bold uppercase tracking-wider ${
              isRunActive
                ? 'bg-cyan-500/10 border-cyan-500/20 text-cyan-400 animate-pulse'
                : latestRun?.status === 'failed'
                ? 'bg-rose-500/10 border-rose-500/20 text-rose-400'
                : latestRun?.status === 'completed_with_errors'
                ? 'bg-amber-500/10 border-amber-500/20 text-amber-400'
                : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
            }`}>
              {latestRun?.status || 'idle'}
            </span>
          </div>

          <div className="grid grid-cols-4 gap-2">
            <div className="bg-slate-950 border border-slate-900 rounded-xl p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Found</div>
              <div className="text-xl font-mono font-bold text-white mt-1">{latestRun?.posts_found ?? 0}</div>
            </div>
            <div className="bg-slate-950 border border-slate-900 rounded-xl p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Done</div>
              <div className="text-xl font-mono font-bold text-emerald-400 mt-1">{latestRun?.posts_processed ?? 0}</div>
            </div>
            <div className="bg-slate-950 border border-slate-900 rounded-xl p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Skip</div>
              <div className="text-xl font-mono font-bold text-amber-400 mt-1">{latestRun?.posts_skipped ?? 0}</div>
            </div>
            <div className="bg-slate-950 border border-slate-900 rounded-xl p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Failed</div>
              <div className="text-xl font-mono font-bold text-rose-400 mt-1">{latestRun?.posts_failed ?? 0}</div>
            </div>
          </div>

          <div className="bg-slate-950 border border-slate-900 rounded-xl p-3 text-xs">
            <div className="flex items-center justify-between gap-3">
              <span className="text-slate-500 uppercase tracking-wider font-bold text-[10px]">Current Step</span>
              {isRunActive ? <RotateCw size={12} className="text-cyan-400 animate-spin" /> : <CheckCircle2 size={12} className="text-emerald-400" />}
            </div>
            <div className="font-mono text-slate-200 mt-1 truncate">{latestRun?.current_step || 'waiting'}</div>
            {latestRun?.error && (
              <div className="mt-2 flex items-start gap-2 text-rose-400">
                <XCircle size={13} className="mt-0.5 flex-shrink-0" />
                <span className="font-mono text-[11px]">{latestRun.error}</span>
              </div>
            )}
          </div>

          <div className="flex-1 bg-slate-950 border border-slate-900 rounded-xl p-4 font-mono text-[11px] text-slate-400 space-y-3 h-[340px] overflow-y-auto">
            {!latestRun && (
              <div className="text-slate-600">// No pipeline runs recorded yet.</div>
            )}

            {latestRun && visibleEvents.length === 0 && (
              <div className="text-slate-600">// Run created. Waiting for events...</div>
            )}

            {visibleEvents.map((event) => (
              <div key={event.id} className="grid grid-cols-[64px_88px_1fr] gap-2 items-start">
                <span className="text-slate-600">{formatEventTime(event.created_at)}</span>
                <span className={`${eventLevelClass(event.level)} uppercase truncate`}>{event.step}</span>
                <span className={event.level === 'error' ? 'text-rose-300' : 'text-slate-300'}>{event.message}</span>
              </div>
            ))}

            {latestRun && !isRunActive && (
              <div className="text-slate-600">// Last run id: {latestRun.id}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
